import React from 'react';
import classNames from 'classnames';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper } from '../../../components';
import type { ParagraphTextMedia as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

interface ParagraphTextMediaProps {
  id?: string;
  entity: ImmutableEntity<Type>;
  noBootstrap?: boolean;
  isWide?: boolean;
}

const ParagraphTextMedia: React.FC<ParagraphTextMediaProps> = ({
  id,
  entity,
  noBootstrap = false,
  isWide = false,
}) => {
  const paragraphTextMedia = entity.toJS();

  const title = paragraphTextMedia.field_title[0]?.value;
  const body = paragraphTextMedia.field_body[0]?.processed || '';

  const link = paragraphTextMedia.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;

  const linkSecondary = paragraphTextMedia.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const ratio = paragraphTextMedia.field_ratio[0]?.value;

  let alignment = paragraphTextMedia.field_alignment[0]?.value;

  const media = paragraphTextMedia.field_media_item[0];

  let caption;
  if (media.bundle !== 'remote_video') {
    caption = media.entity.field_caption[0]?.value;
  }

  let posterImage;
  let altVideoSrc;
  let subtitles;
  let captions;
  if (media.bundle === 'video') {
    posterImage = media.entity.field_poster_image[0]?.url;

    if (media.entity?.field_media_video_file_alt_audio?.[0]) {
      altVideoSrc = {
        src: media.entity.field_media_video_file_alt_audio[0].source,
        language: media.entity.langcode[0].value,
      };
    }

    if (media.entity.field_subtitles[0]) {
      subtitles = {
        src: media.entity.field_subtitles[0].source,
        language: media.entity.langcode[0].value,
      };
    }

    if (media.entity.field_captions[0]) {
      captions = {
        src: media.entity.field_captions[0].source,
        language: media.entity.langcode[0].value,
      };
    }
  }

  if (
    (media.bundle !== 'image') ||
    (!title && !body) ||
    noBootstrap
  ) {
    alignment = 'top';
  }

  const containerClass = classNames(
    'Paragraph',
    'ParagraphTextMedia',
    `ParagraphTextMedia__alignment__${alignment}`,
    {
      'ParagraphTextMedia--flipped': alignment === 'left' && !noBootstrap,
      'ParagraphTextMedia--noBootstrap': noBootstrap,
    },
  );

  let mediaClass = classNames({
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2': (alignment === 'top'),
    'col-12 col-md-4 offset-md-1 col-lg-5 offset-lg-1': (alignment === 'left'),
    'col-12 col-md-4 col-lg-5 offset-lg-1': (alignment === 'right'),
  });

  let bodyClass = classNames({
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2': (alignment === 'top'),
    'col-12 col-md-6 col-lg-4 offset-lg-1': (alignment === 'left'),
    'col-12 col-md-6 offset-md-1 col-lg-4 offset-lg-1': (alignment === 'right'),
  });

  if (noBootstrap) {
    mediaClass = 'noBootstrapColumn';
    bodyClass = 'noBootstrapColumn';
  } else if (isWide) {
    mediaClass = classNames({
      'col-12 col-lg-10 offset-lg-1': (alignment === 'top'),
      'col-12 col-md-5 col-lg-6': (alignment === 'left'),
      'col-12 col-md-5 col-lg-6 offset-lg-1': (alignment === 'right'),
    });
    bodyClass = classNames({
      'col-12 col-lg-8': (alignment === 'top'),
      'col-12 col-md-7 col-lg-5 offset-lg-1': (alignment === 'left'),
      'col-12 col-md-7 col-lg-5': (alignment === 'right'),
    });
  }

  return (
    <div id={id} className={containerClass}>
      <Container>
        <div className={noBootstrap ? 'noBootstrapRow' : 'row'}>
          {(title || body || link || linkSecondary) && (
            <div className={bodyClass}>
              <div className="ParagraphTextMedia__body-wrapper">
                <div className="ParagraphTextMedia__body">
                  <Text
                    title={title}
                    text={body}
                  />
                </div>

                {(link || linkSecondary) && (
                  <div className="ParagraphTextMedia__links">
                    {linkUrl && (
                      <ButtonWrapper
                        className="ParagraphTextMedia__links__link"
                        type="secondary"
                        href={linkUrl}
                      >
                        {linkTitle}
                      </ButtonWrapper>
                    )}
                    {linkSecondaryUrl && (
                      <ButtonWrapper
                        className="ParagraphTextMedia__links__link"
                        type="textual"
                        href={linkSecondaryUrl}
                      >
                        {linkSecondaryTitle}
                      </ButtonWrapper>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={mediaClass}>
            <div className="ParagraphTextMedia__media-wrapper">
              <DynamicMediaItem
                media={media}
                caption={caption}
                imageAspectRatio={
                  ratio === 'default'
                    ? '16_9'
                    : 'original'
                }
                className="ParagraphTextMedia__media"
                posterImage={posterImage}
                altVideoSrc={altVideoSrc}
                subtitles={subtitles}
                captions={captions}
                mediaProps={{
                  'aria-label': media.title,
                }}
              />
            </div>
          </div>
        </div>

      </Container>
    </div>
  );
};

export default ParagraphTextMedia;
